import React, { FC, useCallback, useMemo, useState } from 'react';
import { getStepInputLabel, STEP_INPUT_TYPE_MAP } from '../../../utils/TroubleshootingUtils';
import { extractTextContent, nullSafeGet, nullSafeGetOrElse } from '../../../utils/DataAccessUtils';
import { Button, message, Popconfirm, Popover, Timeline } from 'antd';
import TroubleshootingStepForm from './TroubleshootingStepForm';
import TSStepDisplayInTimeline from './TSStepDisplayInTimeline';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import TroubleshootingAttachmentsDisplay from './TroubleshootingAttachmentsDisplay';
import AccessPermissionChecker from '../../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../../utils/AuthUtils';

const TroubleshootingStepRowDisplay: FC<any> = ({
	step,
	id,
	onEditStep,
	onDeleteStep,
	subStepMCIndex,
	editMode = false,
	onCancelNewStep,
	stepNo,
	isLoading,
}): React.ReactElement => {
	const [editing, setEditing] = useState(false);
	const [mcIndex, setMcIndex] = useState(-1);

	const showMultipleChoices = useMemo(
		() => step.inputType === STEP_INPUT_TYPE_MAP.MULTIPLE_CHOICE,
		[step.inputType]
	);

	const showGroupOptions = useMemo(
		() =>
			step.inputType === STEP_INPUT_TYPE_MAP.GROUP ||
			step.inputType === STEP_INPUT_TYPE_MAP.REPEATABLE_GROUP,
		[step.inputType]
	);

	const _showEditStep = () => setEditing(true);

	const _hideEditStep = () => {
		setEditing(false);
	};

	const onStepEditSuccess = useCallback(
		(editedStep) => {
			return new Promise((resolve, reject) => {
				onEditStep(editedStep, subStepMCIndex)
					.then(resolve)
					.catch(reject)
					.finally(() => _hideEditStep());
			});
		},
		[onEditStep, subStepMCIndex]
	);

	const _deleteStep = () => onDeleteStep && onDeleteStep(step, subStepMCIndex);

	const _addSubStep = useCallback(
		(multipleChoiceIndex) => () => {
			const allMultipleChoices = nullSafeGetOrElse('multipleChoices', step, []);

			const existingNewSubStep = nullSafeGetOrElse(
				`${multipleChoiceIndex}.subSteps`,
				allMultipleChoices,
				[]
			).find((_) => _.isNew);

			if (existingNewSubStep) {
				message.error('Complete the existing substep before adding new one');
				return;
			}

			const updatedMutlipleChoices = allMultipleChoices.map((choice, idx) =>
				idx === multipleChoiceIndex
					? {
							...(choice || {}),
							subSteps: [...nullSafeGetOrElse('subSteps', choice, []), ...[{ isNew: true }]],
					  }
					: choice
			);

			const updatedStep = {
				...step,
				multipleChoices: updatedMutlipleChoices,
			};
			setMcIndex(multipleChoiceIndex);
			return onStepEditSuccess(updatedStep);
		},
		[onStepEditSuccess, step]
	);

	const updateWithNewSubSteps = useCallback(
		(existingMultipleChoices, currentIndex, subSteps) => {
			const stepWithSubSteps = {
				...step,
				multipleChoices: existingMultipleChoices.map((mc, idx) =>
					idx === currentIndex ? { ...mc, subSteps } : mc
				),
			};
			return onStepEditSuccess(stepWithSubSteps);
		},
		[onStepEditSuccess, step]
	);

	const onNewSubStepAddSuccess = useCallback(
		(addedSubStep) => {
			const allMultipleChoices = nullSafeGetOrElse('multipleChoices', step, []);
			const updatedMutlipleChoices = allMultipleChoices.map((choice, idx) => {
				const existingSubSteps = nullSafeGetOrElse('subSteps', choice, []);
				return idx === mcIndex
					? {
							...(choice || {}),
							subSteps: existingSubSteps.map((subStep) =>
								subStep.isNew
									? {
											...addedSubStep,
											orderingId: existingSubSteps.length + 1,
									  }
									: subStep
							),
					  }
					: choice;
			});
			const updatedStep = {
				...step,
				multipleChoices: updatedMutlipleChoices,
			};
			return onStepEditSuccess(updatedStep);
		},
		[mcIndex, onStepEditSuccess, step]
	);

	const onSubStepEditSuccess = useCallback(
		(subStep, multipleChoiceIndex) => {
			if (!(multipleChoiceIndex > -1)) {
				console.log('Illegal onSubStepEditSuccess called!!');
				return;
			}

			const existingMultipleChoices = nullSafeGetOrElse('multipleChoices', step, []);
			const existingSubSteps = nullSafeGetOrElse(
				`${multipleChoiceIndex}.subSteps`,
				existingMultipleChoices,
				[]
			);
			const subSteps = !!subStep.orderingId
				? existingSubSteps.map((_) => (_.orderingId === subStep.orderingId ? subStep : _))
				: [
						...existingSubSteps,
						...[
							{
								...subStep,
								orderingId: existingSubSteps.length + 1,
							},
						],
				  ];
			return updateWithNewSubSteps(existingMultipleChoices, multipleChoiceIndex, subSteps);
		},
		[step, updateWithNewSubSteps]
	);

	const onSubStepDelete = useCallback(
		(subStep, multipleChoiceIndex) => {
			const existingMultipleChoices = nullSafeGetOrElse('multipleChoices', step, []);
			const existingSubSteps = nullSafeGetOrElse(
				`${multipleChoiceIndex}.subSteps`,
				existingMultipleChoices,
				[]
			);
			const subSteps = existingSubSteps
				.sort((a, b) => parseInt(a.orderingId || 0) - parseInt(b.orderingId || 0))
				.filter((_) => _.orderingId !== subStep.orderingId)
				.map((_, idx) => ({ ..._, orderingId: idx + 1 }));
			updateWithNewSubSteps(existingMultipleChoices, multipleChoiceIndex, subSteps);
		},
		[step, updateWithNewSubSteps]
	);

	const onSubStepCancel = useCallback(() => {
		return onStepEditSuccess({
			...step,
			multipleChoices: nullSafeGetOrElse('multipleChoices', step, []).map((choice) => ({
				...choice,
				subSteps: nullSafeGetOrElse('subSteps', choice, []).filter((_) => !_.isNew),
			})),
		});
	}, [onStepEditSuccess, step]);

	const inputLabel = useMemo(() => {
		const inputTypeLabel =
			step.inputType === STEP_INPUT_TYPE_MAP.MULTIPLE_CHOICE && step.allowMultipleAnswerSelections
				? 'Multiple choice & multiple answers'
				: getStepInputLabel(step.inputType) || 'None';
		return step.inputRequired
			? `${inputTypeLabel} input (${step.userCanSkipInput ? 'optional' : 'mandatory'})`
			: '';
	}, [step]);

	const inputDescription = useMemo(
		() => (
			<div className="text-sm text-gray-400">
				{step.canProceedToNextStep ? (
					inputLabel
				) : (
					<div className="text-[rgb(255,99,104)]">Next step: Not Allowed</div>
				)}
			</div>
		),
		[inputLabel, step.canProceedToNextStep]
	);

	const isEditMode = useMemo(() => editing || step.isNew, [editing, step.isNew]);

	return (
		<TSStepDisplayInTimeline stepNo={stepNo} editMode={isEditMode}>
			{isEditMode ? (
				<div className="ml-2">
					<TroubleshootingStepForm
						step={step}
						onCancel={step.isNew ? onCancelNewStep : _hideEditStep}
						onSuccess={onStepEditSuccess}
					/>
				</div>
			) : (
				<div className="ml-2 flex flex-col">
					<div className="flex flex-row items-start gap-2">
						<div className="flex flex-col">
							<div className="flex flex-row items-center gap-2">
								{extractTextContent(nullSafeGetOrElse('title', step, ''))}
								{editMode ? (
									<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_TROUBLESHSHOOTING}>
										<div>
											<Popover trigger="hover" content="Edit Step">
												<Button
													icon={<EditOutlined />}
													size="small"
													type="link"
													onClick={_showEditStep}
												/>
											</Popover>
											<Popconfirm
												onConfirm={_deleteStep}
												placement="topLeft"
												title="Are you sure you want to delete?"
												okText="Yes"
												cancelText="No"
											>
												<Popover trigger="hover" content="Delete Step">
													<Button icon={<DeleteOutlined />} size="small" type="link" />
												</Popover>
											</Popconfirm>
										</div>
									</AccessPermissionChecker>
								) : null}
							</div>
							{step.description ? (
								<div
									className="richTextWrapper text-gray-400"
									dangerouslySetInnerHTML={{ __html: step.description }}
								/>
							) : null}
							{inputDescription}
						</div>
						<TroubleshootingAttachmentsDisplay
							attachments={nullSafeGetOrElse('attachments', step, [])}
						/>
					</div>
					{showMultipleChoices ? (
						<div className="mt-8">
							<Timeline>
								{nullSafeGetOrElse('multipleChoices', step, []).map((choice, sIdx) => {
									const choiceNumber = `${stepNo}.${sIdx + 1}`;
									return (
										<Timeline.Item position="right">
											<TSStepDisplayInTimeline stepNo={choiceNumber}>
												<div className="flex flex-col">
													<div className="flex flex-row items-baseline">
														<div className="ml-2">
															{`${
																(choice as any).description
																	? `${(choice as any).description} - `
																	: ''
															} ${(choice as any).value}`}
														</div>
														{editMode ? (
															<AccessPermissionChecker
																name={PERMISSION_NAMES.MODIFY_TROUBLESHSHOOTING}
															>
																<div className="ml-2">
																	<Popover trigger="hover" content="Add Sub Step">
																		<Button
																			icon={<PlusOutlined />}
																			type="link"
																			size="small"
																			onClick={_addSubStep(sIdx)}
																		/>
																	</Popover>
																</div>
															</AccessPermissionChecker>
														) : null}
													</div>
													{nullSafeGet('subSteps.0', choice) ? (
														<div className="ml-6 mt-4">
															<Timeline>
																{nullSafeGetOrElse('subSteps', choice, []).map((subStep, ssIdx) => {
																	const key = `${id || step.orderingId}-subStep-${ssIdx}`;
																	const currentStepNumber = `${choiceNumber}.${ssIdx + 1}`;
																	return (
																		<Timeline.Item position="right">
																			{nullSafeGet('isNew', subStep) ? (
																				<TSStepDisplayInTimeline
																					stepNo={currentStepNumber}
																					editMode={nullSafeGet('isNew', subStep)}
																				>
																					<TroubleshootingStepForm
																						step={{}}
																						onCancel={onSubStepCancel}
																						onSuccess={onNewSubStepAddSuccess}
																					/>
																				</TSStepDisplayInTimeline>
																			) : (
																				<TroubleshootingStepRowDisplay
																					key={key}
																					id={key}
																					step={subStep}
																					stepNo={currentStepNumber}
																					subStepMCIndex={sIdx}
																					onEditStep={onSubStepEditSuccess}
																					onDeleteStep={onSubStepDelete}
																					editMode={editMode}
																				/>
																			)}
																		</Timeline.Item>
																	);
																})}
															</Timeline>
														</div>
													) : null}
												</div>
											</TSStepDisplayInTimeline>
										</Timeline.Item>
									);
								})}
							</Timeline>
						</div>
					) : null}
					{showGroupOptions ? (
						<div className="mt-4 flex flex-col gap-1 ">
							{nullSafeGetOrElse('groupFields', step, []).map((field, idx) => {
								const options =
									field.inputType === STEP_INPUT_TYPE_MAP.MULTIPLE_CHOICE
										? nullSafeGetOrElse('options', field, [])
										: [];
								const optionString = options.length > 0 ? `(${options.join(', ')})` : '';

								return (
									<div
										key={`step-${id}-group-field-${idx}`}
										className="flex flex-row items-start gap-2"
									>
										<div>{idx + 1}.</div>
										<div className="flex flex-col">
											<span>{extractTextContent(nullSafeGetOrElse('label', field, ''))}</span>
											<span className="text-sm text-gray-400">
												{getStepInputLabel(field.inputType)} {optionString} (
												{field.required ? 'mandatory' : 'optional'})
											</span>
										</div>
									</div>
								);
							})}
						</div>
					) : null}
				</div>
			)}
		</TSStepDisplayInTimeline>
	);
};

export default TroubleshootingStepRowDisplay;
